import { Controller } from "@hotwired/stimulus"

// Page-wide, because one dead Google breaks every field at once. While degraded we stop asking
// and the submit guard stops blocking, so a lookup outage never makes a form unusable.
const degraded = { active: false, failures: 0, retryAt: 0 }

// Google answered, and answered correctly - these say something about the input, not about
// Google's health, so they must never count toward a degrade.
const BENIGN_ERRORS = ["not_found", "wrong_kind", "outside_us"]

// Distinguishes "we cancelled this ourselves" from "this failed", which a bare null cannot.
const ABORTED = Symbol("aborted")

// How the session token keeps the Google bill down:
//
// Google bills autocomplete one of two ways. Every request separately, or - when all the requests
// from one bout of typing carry the same sessionToken and end in a details call - once for the
// whole session, no matter how many keystrokes it took. So we mint one token on the first request,
// send that same token with every keystroke after it, send it once more with the details call for
// whatever the user picks, and then drop it. One field, one pick, one charge.
//
// Three rules follow, and all three are load-bearing:
//   1. Never change the token mid-session (each new token starts a new billable session).
//   2. Always end the session with a details call (an abandoned session is billed per request).
//   3. Never reuse a token after its details call (Google closed that session; see pick()).
//
// Careful: "token" means two unrelated things in this feature. This one is Google's billing ID.
// The `place-token` controller paired with this one in the FFS and territory views is the visual
// chip drawn in the input, and has nothing to do with billing.

// Mirrors Constants::Google::PlacesFields::STREET_ADDRESS. The only field whose value legitimately
// grows past what Google resolved (see stillVerified).
const STREET_ADDRESS = "street_address"

const DEGRADE_AFTER_FAILURES = 2
const DEGRADE_FOR_MS = 60000
const DEBOUNCE_MS = 250
const LISTBOX_OWNER_ATTRIBUTE = "data-place-listbox-owner"

let instanceCount = 0

document.addEventListener("turbo:before-cache", () => {
  document.querySelectorAll(`[${LISTBOX_OWNER_ATTRIBUTE}]`).forEach((node) => node.remove())
})

// Fill targets that already un-verify themselves on a hand change. A WeakSet, not a data
// attribute: a Turbo restore would bring the attribute back without the listener behind it.
const watchedSelects = new WeakSet()

export default class extends Controller {
  static targets = ["input", "placeId", "error", "suggestion"]
  static values = {
    field: String,
    minLength: { type: Number, default: 3 },
    scope: { type: String, default: "form" },
    fill: { type: Object, default: {} },
    autocompleteUrl: String,
    detailsUrl: String
  }

  connect() {
    this.ownerKey = `place-listbox-${++instanceCount}`
    // No billing session until the user types something; post() mints the token on first use.
    this.sessionToken = null
    this.suggestions = []
    this.activeIndex = -1
    this.listbox = null
    this.controller = null
    this.timer = null

    this.reposition = this.reposition.bind(this)
    this.reconcile()
    this.watchFillSelects()
    this.applyAria()
    // `degraded` is module state and outlives a Turbo visit; the notice is re-rendered hidden.
    // Without this the page refuses every lookup while the submit guard stays armed.
    if (degraded.active) this.showNotice()
  }

  disconnect() {
    this.abort()
    clearTimeout(this.timer)
    this.closeList()
  }

  // A cloned column, or a Turbo restore, brings markup back with no JS state behind it. An empty
  // field is never verified, which is what stops a clone inheriting the source column's pick.
  //
  // Never cascades: on connect this field has filled nothing yet, so its fill targets hold what
  // the server rendered. Clearing them here wiped a saved city/state/ZIP whenever the address
  // field above them happened to be blank.
  reconcile() {
    document.querySelectorAll(`[${LISTBOX_OWNER_ATTRIBUTE}="${this.ownerKey}"]`).forEach((n) => n.remove())

    const input = this.inputTarget
    if (!input.value.trim()) {
      this.invalidate({ cascade: false })
      this.hideSuggestion()
      return
    }
    this.verifiedValue = input.dataset.placeVerified === "true" ? input.value : null
    if (this.verifiedValue) this.paintVerified(true)
    else this.invalidate({ cascade: false })
  }

  applyAria() {
    const input = this.inputTarget
    input.setAttribute("role", "combobox")
    input.setAttribute("aria-autocomplete", "list")
    input.setAttribute("aria-expanded", "false")
    input.setAttribute("autocomplete", "off")
    // A cloned column carries these pointing at the source column's listbox; they are re-set when
    // this instance opens its own.
    input.removeAttribute("aria-controls")
    input.removeAttribute("aria-activedescendant")
    this.status = this.buildStatusNode()
  }

  buildStatusNode() {
    const existing = this.element.querySelector(":scope > .place-autocomplete-status")
    if (existing) {
      existing.textContent = ""
      return existing
    }

    const node = document.createElement("div")
    node.className = "place-autocomplete-status"
    node.setAttribute("role", "status")
    node.setAttribute("aria-live", "polite")
    this.element.appendChild(node)
    return node
  }

  announce(message) {
    if (this.status) this.status.textContent = message
  }

  // --- events -------------------------------------------------------------

  onInput() {
    const value = this.inputTarget.value
    if (!this.stillVerified(value)) this.invalidate()
    // The server's hint quotes the rendered text, so it stops applying once that text is edited.
    if (value !== this.inputTarget.defaultValue) this.hideSuggestion()

    clearTimeout(this.timer)
    if (value.trim().length < this.minLengthFor(value)) {
      this.closeList()
      return
    }
    this.timer = setTimeout(() => this.search(value.trim()), DEBOUNCE_MS)
  }

  // Unit, apartment and suite numbers are appended to the street line Google resolved and say
  // nothing about the city, state or ZIP, so a street address stays verified while it only grows.
  // Every other field's value IS the answer we store, so there only an exact match holds.
  stillVerified(value) {
    if (!this.verifiedValue) return false
    if (value === this.verifiedValue) return true

    return this.fieldValue === STREET_ADDRESS && value.startsWith(this.verifiedValue)
  }

  // Reopening a stale suggestion list would mean re-creating the listbox without repainting it
  // (paint() only runs from render()), which draws an empty bordered box under the input. Leaving
  // it closed also means ArrowDown below sees `open` as false and runs a fresh search instead of
  // moving a highlight through zero rows.
  onFocus() {}

  onFocusOut() {
    // A pick runs on mousedown, so focus never leaves the input for a click on a suggestion.
    // The queued search has to go too, or it reopens a listbox against an input nobody is in.
    clearTimeout(this.timer)
    this.closeList()
    if (this.inputTarget.value.trim() && !this.verifiedValue) this.showError()
  }

  onKeyDown(event) {
    const open = Boolean(this.listbox)

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        open ? this.move(1) : this.onInput()
        break
      case "ArrowUp":
        if (!open) return
        event.preventDefault()
        this.move(-1)
        break
      case "Home":
        if (!open) return
        event.preventDefault()
        this.highlight(0)
        break
      case "End":
        if (!open) return
        event.preventDefault()
        this.highlight(this.suggestions.length - 1)
        break
      case "Enter":
        // Claims Enter before the page's form-level handler sees it, so a pick never submits.
        if (!open || this.activeIndex < 0) return
        event.preventDefault()
        this.pick(this.suggestions[this.activeIndex])
        break
      case "Escape":
        // Closes the list, then abandons an in-progress edit by putting the resolved place back.
        // It never empties the field: that is the dismiss button's job, and a key people press
        // reflexively must not be the one that destroys four fields at once.
        if (open) {
          event.preventDefault()
          this.closeList()
        } else if (this.revertable()) {
          event.preventDefault()
          this.revert()
        }
        break
      default:
        break
    }
  }

  // Another field resolved a place that includes this one's value.
  onFilled(event) {
    const { value, placeId } = event.detail
    // The resolved place said nothing about this field. Drop the verification, but do not cascade:
    // the pick driving this fill is mid-pass over these same fields, and our clearFill would blank
    // what it just wrote (a place with no postal_code used to wipe the city and state with it).
    if (!value) {
      this.invalidate({ cascade: false })
      return
    }

    this.inputTarget.value = value
    this.markVerified(value, placeId)
  }

  // --- lookup -------------------------------------------------------------

  minLengthFor(value) {
    return /^\d+$/.test(value.trim()) ? 5 : this.minLengthValue
  }

  async search(query) {
    if (this.blocked()) return

    this.announce("Searching…")
    const body = await this.post(this.autocompleteUrlValue, { query, field: this.fieldValue })
    if (body === ABORTED) return
    if (!body) {
      // A lookup failure below the degrade threshold leaves no outage notice to explain it, so
      // this keystroke's "Searching…" needs its own resolution.
      //
      // The open list belongs to the query this one replaced, so it has to go with it: left up, it
      // keeps offering predictions for text the field no longer contains.
      this.closeList()
      this.announce("Search failed. Try again.")
      return
    }

    this.render(body.suggestions || [])
  }

  async pick(suggestion) {
    // A keystroke queued just before this click would fire mid-details and abort the very request
    // the pick depends on, flagging the field invalid for a request we cancelled ourselves.
    clearTimeout(this.timer)
    this.inputTarget.value = suggestion.primary_text || suggestion.text || ""
    this.closeList()

    const body = await this.post(this.detailsUrlValue, {
      place_id: suggestion.place_id,
      field: this.fieldValue
    })
    // Typing through the lookup aborts it. The pick is stale and the session belongs to the search
    // that replaced it, so leave both to the new input.
    if (body === ABORTED) return

    // The session ends with the details call whether or not it succeeded - Google has closed it
    // either way, so clear the token and let the next keystroke start (and pay for) a new one.
    this.sessionToken = null

    const place = body && body.place
    if (!place) {
      // While degraded the notice already explains it, and "choose from the suggestions" is the
      // wrong thing to say when there are none to choose from.
      if (!degraded.active) this.showError()
      return
    }

    const value = this.valueFor(place)
    this.inputTarget.value = value

    // A required field's companion select (state) has to actually resolve, or a "verified" green
    // token would misrepresent a pick Google gave us a state for that we can't store - e.g. a US
    // territory's admin_area_level_1 code isn't one of the 51 rows in the `states` table.
    if (!this.applyFill(place) && this.inputTarget.required) {
      this.invalidate()
      this.showError()
      this.announce(`${place.formatted_address || value} could not be matched to a supported state.`)
      return
    }

    this.resolvedPlace = place
    this.markVerified(value, place.place_id)
    this.announce(`Selected ${place.formatted_address || value}.`)
    // A richer display (e.g. the territory token/scope toggle) reacts to the resolved place
    // without duplicating the lookup itself.
    this.inputTarget.dispatchEvent(new CustomEvent("place:picked", { detail: { place }, bubbles: true }))
  }

  // The server's "Use <name>" hint. String(): Stimulus JSON-parses params, so a numeric value arrives a Number.
  acceptSuggestion(event) {
    const { placeId, text } = event.params
    this.pick({ place_id: String(placeId), primary_text: String(text) })
  }

  valueFor(place) {
    if (this.fieldValue === "street_address") return place.street_line || place.formatted_address || ""
    if (this.fieldValue === "postal_code") return place.postal_code || ""
    // A ZIP pick is just a faster way to find the right city here - unlike "territory", it never
    // stores the ZIP itself.
    if (this.fieldValue === "city" || this.fieldValue === "city_via_zip_or_name") return place.city || ""
    return place.territory_value || ""
  }

  // @return [Boolean] false when a SELECT target (e.g. state) had a value to match but none of its
  // options matched - the caller decides whether that blocks verification.
  applyFill(place) {
    let matched = true

    Object.entries(this.fillValue).forEach(([key, selector]) => {
      const node = this.scopeElement()?.querySelector(selector)
      if (!node || node === this.inputTarget) return

      const value = place[key]

      if (node.tagName === "SELECT") {
        // A US place kind (city/ZIP/street) essentially always has a state; a blank result here
        // is Collector's `State.find_by` failing to map Google's code, not a place that has none.
        const selected = this.setSelect(node, value)
        if (!selected) matched = false
        this.paintFilledSelect(node, selected)
        return
      }

      // Written even when blank: leaving the previous place's city sitting under a new address is
      // the one failure the user cannot see, because nothing about the field says it went stale.
      node.value = value || ""
      // Dispatched even with nothing to fill: it is what lets a filled field that is itself guarded
      // count as verified, and what drops that verification when the place cannot supply it.
      node.dispatchEvent(new CustomEvent("place:filled", {
        detail: { value: value || null, placeId: place.place_id }
      }))
    })

    return matched
  }

  // @return [Boolean] whether an option actually matched `value`
  setSelect(select, value) {
    const options = Array.from(select.options)
    const match = value && (options.find((o) => o.value === value) ||
      options.find((o) => o.value.toLowerCase() === String(value).toLowerCase()))

    // An unresolved state left the previous pick's selection sitting beside a new city, and the
    // guard cannot see a select. Reset instead, and let the field's own `required` hold the form.
    select.value = match ? match.value : ""
    select.dispatchEvent(new Event("change", { bubbles: true }))
    return Boolean(match)
  }

  // Drops the fields' verification without touching their values. What an edit in progress needs:
  // the checks come off because this pick no longer vouches for them, but a half-typed street
  // address must not empty a required city the user would then have to reconstruct.
  unverifyFill() {
    this.eachFillTarget((node) => {
      if (node.tagName === "SELECT") {
        this.paintFilledSelect(node, false)
        return
      }
      node.dispatchEvent(new CustomEvent("place:filled", { detail: { value: null, placeId: null } }))
    })
  }

  // The fill belongs to whichever pick verified this field; once that pick is thrown away, leaving
  // the old state/etc. behind would submit a location that no longer matches it.
  clearFill() {
    this.eachFillTarget((node) => {
      node.value = ""
      if (node.tagName === "SELECT") {
        this.paintFilledSelect(node, false)
        node.dispatchEvent(new Event("change", { bubbles: true }))
        return
      }
      node.dispatchEvent(new CustomEvent("place:filled", { detail: { value: null, placeId: null } }))
    })
  }

  eachFillTarget(callback) {
    Object.values(this.fillValue).forEach((selector) => {
      const node = this.scopeElement()?.querySelector(selector)
      if (node && node !== this.inputTarget) callback(node)
    })
  }

  scopeElement() {
    return this.element.closest(this.scopeValue) || this.element.closest("form") || document
  }

  // --- verification state -------------------------------------------------

  markVerified(value, placeId) {
    this.verifiedValue = value
    // Survives invalidate(), which is the point: it is what an abandoned edit reverts to.
    this.resolvedValue = value
    this.paintVerified(true)
    if (this.hasPlaceIdTarget) this.placeIdTarget.value = placeId || ""
    this.clearError()
    this.hideSuggestion()
  }

  hideSuggestion() {
    if (this.hasSuggestionTarget) this.suggestionTarget.classList.add("d-none")
  }

  // @param cascade [Boolean] whether to touch the fields this field's pick filled at all. False
  //   when the invalidation came from another field's fill, which owns those fields for that pass.
  // @param erase [Boolean] whether to blank them as well as un-vouch for them. Only true when the
  //   user threw the pick away outright - mid-edit, a stale city beats an empty required one.
  invalidate({ cascade = true, erase = false } = {}) {
    this.verifiedValue = null
    this.paintVerified(false)
    if (this.hasPlaceIdTarget) this.placeIdTarget.value = ""
    this.clearError()
    if (cascade) erase ? this.clearFill() : this.unverifyFill()
    this.inputTarget.dispatchEvent(new CustomEvent("place:invalidated", { bubbles: true }))
  }

  // Throws the pick away: the value, the fill, and the place an edit could have reverted to.
  discard() {
    this.resolvedPlace = null
    this.resolvedValue = null
    this.inputTarget.value = ""
    this.invalidate({ erase: true })
  }

  // Anything that took the field out of its resolved state is revertable, not just typing: the
  // pencil un-verifies without changing a character, and Escape has to undo that too.
  revertable() {
    return Boolean(this.resolvedPlace) && !this.verifiedValue
  }

  // Re-runs the tail of the pick that produced this place, with no second lookup.
  revert() {
    this.inputTarget.value = this.resolvedValue
    this.applyFill(this.resolvedPlace)
    this.markVerified(this.resolvedValue, this.resolvedPlace.place_id)
    this.announce(`Restored ${this.resolvedValue}.`)
    this.inputTarget.dispatchEvent(new CustomEvent("place:picked", {
      detail: { place: this.resolvedPlace }, bubbles: true
    }))
  }

  // The green check. Where a token replaces the input (territory checks, the survey's location
  // preferences) the input is `d-none` by then, so only the token-less fields actually show it.
  paintVerified(verified) {
    this.inputTarget.dataset.placeVerified = verified ? "true" : "false"
    this.inputTarget.classList.toggle("is-valid", verified)
    if (verified) this.inputTarget.classList.remove("is-invalid")
  }

  // A filled SELECT has no controller of its own, so its check is painted from here.
  paintFilledSelect(select, verified) {
    select.dataset.placeVerified = verified ? "true" : "false"
    select.classList.toggle("is-valid", verified)
  }

  // Watched from load, not from the first fill: the server seeds a saved state as verified too,
  // and that check has to come off the moment the user picks a different one.
  watchFillSelects() {
    Object.values(this.fillValue).forEach((selector) => {
      const node = this.scopeElement()?.querySelector(selector)
      if (node?.tagName === "SELECT") this.watchFilledSelect(node)
    })
  }

  // Choosing a state by hand makes it the user's answer, not the pick's, so the check comes off.
  // setSelect's own change event is synthetic, and isTrusted is what tells the two apart.
  watchFilledSelect(select) {
    if (watchedSelects.has(select)) return

    watchedSelects.add(select)
    select.addEventListener("change", (event) => {
      if (event.isTrusted) this.paintFilledSelect(select, false)
    })
  }

  // Lets a richer display (the territory scope toggle) swap the input's text between two
  // already-resolved representations of the same pick without re-running verification.
  setVerifiedValue(value) {
    this.inputTarget.value = value
    this.markVerified(value, this.hasPlaceIdTarget ? this.placeIdTarget.value : null)
  }

  showError() {
    this.inputTarget.classList.add("is-invalid")
  }

  clearError() {
    this.inputTarget.classList.remove("is-invalid")
  }

  // --- transport ----------------------------------------------------------

  blocked() {
    if (!degraded.active) return false
    if (Date.now() < degraded.retryAt) return true

    // Let one user-driven probe through per cooldown, rather than polling. Staying `active` until
    // the probe actually succeeds is what lets recover() know there is a notice to take down.
    degraded.retryAt = Date.now() + DEGRADE_FOR_MS
    return false
  }

  async post(url, params) {
    this.abort()
    this.controller = new AbortController()
    // Every request goes through here, so this one line is what keeps the whole session - each
    // keystroke and the details call that ends it - on a single token. Mint only if we have none.
    this.sessionToken = this.sessionToken || this.newSessionToken()

    const data = new URLSearchParams({ ...params, session_token: this.sessionToken })

    try {
      const response = await fetch(url, {
        method: "POST",
        signal: this.controller.signal,
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": this.csrfToken(),
          Accept: "application/json"
        },
        body: data
      })

      if (response.status === 429) {
        this.degrade(true)
        return null
      }
      if (!response.ok) {
        this.degrade(false)
        return null
      }

      const body = await response.json()
      if (body.error && !BENIGN_ERRORS.includes(body.error)) {
        this.degrade(body.error === "rate_limited")
        return null
      }

      this.recover()
      return body
    } catch (error) {
      if (error.name === "AbortError") return ABORTED
      this.degrade(false)
      return null
    }
  }

  abort() {
    if (this.controller) this.controller.abort()
    this.controller = null
  }

  degrade(immediate) {
    degraded.failures += 1
    if (!immediate && degraded.failures < DEGRADE_AFTER_FAILURES) return

    degraded.active = true
    degraded.retryAt = Date.now() + DEGRADE_FOR_MS
    this.closeList()
    this.showNotice()
  }

  recover() {
    degraded.failures = 0
    if (!degraded.active) return

    degraded.active = false
    this.hideNotice()
  }

  showNotice() {
    document.querySelectorAll(".place-autocomplete-notice").forEach((n) => n.classList.remove("d-none"))
  }

  hideNotice() {
    document.querySelectorAll(".place-autocomplete-notice").forEach((n) => n.classList.add("d-none"))
  }

  // Nothing outside this controller ever has to think about the session token, and that is on
  // purpose. It is minted here, kept on the instance, attached by post(), and dropped by pick() -
  // so search() and pick() never mention it, no view passes it, and adding a new autocomplete
  // field needs no billing code at all. Wiring up the Stimulus controller is the whole job.
  //
  // The cost of that is one rule: any new request must go through post(). Call fetch() directly
  // and it leaves the session, which is a silent overcharge rather than a visible bug.
  //
  // Google only needs this to be unique and opaque, so any random value will do. Returning null on
  // a browser with no crypto is a deliberate give-up: it serializes as "null", the server's format
  // check drops it, and that session simply gets billed per request instead of failing.
  newSessionToken() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID()
    if (window.crypto && window.crypto.getRandomValues) {
      const bytes = window.crypto.getRandomValues(new Uint8Array(16))
      return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")
    }
    return null
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  // --- listbox ------------------------------------------------------------

  render(suggestions) {
    this.suggestions = suggestions
    this.activeIndex = suggestions.length ? 0 : -1

    if (!suggestions.length) {
      this.announce("No matches found.")
      this.openList()
      this.paintEmpty()
      return
    }

    this.announce(`${suggestions.length} ${suggestions.length === 1 ? "suggestion" : "suggestions"} available. Use up and down arrow keys to review.`)
    this.openList()
    this.paint()
  }

  openList() {
    if (!this.listbox) {
      this.listbox = document.createElement("div")
      this.listbox.className = "dropdown-menu show place-autocomplete-listbox"
      this.listbox.id = this.ownerKey
      this.listbox.setAttribute("role", "listbox")
      this.listbox.setAttribute(LISTBOX_OWNER_ATTRIBUTE, this.ownerKey)
      document.body.appendChild(this.listbox)

      this.inputTarget.setAttribute("aria-controls", this.ownerKey)
      this.inputTarget.setAttribute("aria-expanded", "true")
      window.addEventListener("scroll", this.reposition, true)
      window.addEventListener("resize", this.reposition)
    }
    this.reposition()
  }

  closeList() {
    if (!this.listbox) return

    window.removeEventListener("scroll", this.reposition, true)
    window.removeEventListener("resize", this.reposition)
    this.listbox.remove()
    this.listbox = null
    this.activeIndex = -1
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
  }

  reposition() {
    if (!this.listbox) return

    const rect = this.inputTarget.getBoundingClientRect()
    // Scrolled out of its own container: a dropdown floating over unrelated content is worse
    // than no dropdown.
    if (rect.bottom < 0 || rect.top > window.innerHeight) {
      this.closeList()
      return
    }

    this.listbox.style.top = `${rect.bottom}px`
    this.listbox.style.left = `${rect.left}px`
    this.listbox.style.minWidth = `${rect.width}px`
  }

  paintEmpty() {
    this.listbox.replaceChildren()
    const empty = document.createElement("div")
    empty.className = "place-autocomplete-empty"
    empty.textContent = "No matches found."
    this.listbox.appendChild(empty)
  }

  // Built as nodes with textContent, never markup: suggestion text is third-party data.
  paint() {
    this.listbox.replaceChildren()

    this.suggestions.forEach((suggestion, index) => {
      const option = document.createElement("button")
      option.type = "button"
      option.className = "dropdown-item place-autocomplete-suggestion"
      option.id = `${this.ownerKey}-option-${index}`
      option.setAttribute("role", "option")

      const primary = document.createElement("span")
      primary.className = "place-autocomplete-suggestion-primary"
      primary.textContent = suggestion.primary_text || suggestion.text || ""
      option.appendChild(primary)

      if (suggestion.secondary_text) {
        const secondary = document.createElement("span")
        secondary.className = "place-autocomplete-suggestion-secondary"
        secondary.textContent = suggestion.secondary_text
        option.appendChild(secondary)
      }

      option.addEventListener("mousedown", (event) => {
        event.preventDefault()
        this.pick(suggestion)
      })
      option.addEventListener("mouseenter", () => this.highlight(index))

      this.listbox.appendChild(option)
    })

    this.paintActive()
  }

  move(delta) {
    if (!this.suggestions.length) return

    const count = this.suggestions.length
    this.highlight((this.activeIndex + delta + count) % count)
  }

  highlight(index) {
    this.activeIndex = index
    this.paintActive()
  }

  paintActive() {
    if (!this.listbox) return

    Array.from(this.listbox.children).forEach((node, index) => {
      const active = index === this.activeIndex
      node.classList.toggle("active", active)
      node.setAttribute("aria-selected", active ? "true" : "false")
      if (active) this.inputTarget.setAttribute("aria-activedescendant", node.id)
    })
  }
}
